// Offline outbox for serial/queue actions (call next, complete, skip, recall...).
//
// While the network is down the serial screen keeps working: every status change
// is written to IndexedDB with a monotonically increasing sequence number and
// applied locally as an overlay. When connectivity returns the queued actions are
// replayed in order; the last queued status for an appointment wins, so replaying
// is idempotent even if a write partially succeeded before the drop.

import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import { supabase } from "@/integrations/supabase/client";
import { trackSync } from "@/lib/sync-audit";

const DB_NAME = "medidesk-queue-outbox";
const DB_VERSION = 2;
const STORE = "pending_queue_actions";
const DRAFT_STORE = "pending_appointments";

export interface PendingQueueAction {
  id: string;                // uuid of the queued action
  appointment_id: string;
  status: string;            // target status
  action: string;            // audit action label
  from_status?: string | null;
  patient_name?: string | null;
  serial_number?: number | null;
  seq: number;               // ordering across the outbox
  queued_at: number;
  attempts: number;
  last_error?: string;
}

/** A walk-in / booking captured while offline. `id` is the future appointment row id. */
export interface PendingAppointmentDraft {
  id: string;
  user_id: string;
  patient_id: string;
  patient_name?: string | null;
  date: string;
  time: string;
  type: string;
  chamber_id?: string | null;
  seq: number;
  queued_at: number;
  attempts: number;
  last_error?: string;
}

interface QueueSchema extends DBSchema {
  pending_queue_actions: {
    key: string;
    value: PendingQueueAction;
    indexes: { by_seq: number };
  };
  pending_appointments: {
    key: string;
    value: PendingAppointmentDraft;
    indexes: { by_seq: number };
  };
}

let dbPromise: Promise<IDBPDatabase<QueueSchema>> | null = null;

function getDb() {
  if (!dbPromise) {
    dbPromise = openDB<QueueSchema>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE)) {
          const store = db.createObjectStore(STORE, { keyPath: "id" });
          store.createIndex("by_seq", "seq");
        }
        if (!db.objectStoreNames.contains(DRAFT_STORE)) {
          const drafts = db.createObjectStore(DRAFT_STORE, { keyPath: "id" });
          drafts.createIndex("by_seq", "seq");
        }
      },

    });
  }
  return dbPromise;
}

type Listener = (items: PendingQueueAction[]) => void;
type DraftListener = (items: PendingAppointmentDraft[]) => void;
const listeners = new Set<Listener>();
const draftListeners = new Set<DraftListener>();
const channel =
  typeof BroadcastChannel !== "undefined" ? new BroadcastChannel("medidesk-queue-outbox") : null;

async function notify() {
  const [items, drafts] = await Promise.all([
    listPendingQueueActions(),
    listPendingAppointments(),
  ]);
  listeners.forEach((l) => l(items));
  draftListeners.forEach((l) => l(drafts));
}

async function emit() {
  await notify();
  channel?.postMessage({ type: "changed" });
}

channel?.addEventListener("message", () => { void notify(); });

export function subscribeQueueOutbox(fn: Listener): () => void {
  listeners.add(fn);
  listPendingQueueActions().then(fn);
  return () => { listeners.delete(fn); };
}

export function subscribeQueueDrafts(fn: DraftListener): () => void {
  draftListeners.add(fn);
  listPendingAppointments().then(fn);
  return () => { draftListeners.delete(fn); };
}

export async function listPendingQueueActions(): Promise<PendingQueueAction[]> {
  const db = await getDb();
  const all = await db.getAll(STORE);
  return all.sort((a, b) => a.seq - b.seq);
}

export async function listPendingAppointments(): Promise<PendingAppointmentDraft[]> {
  const db = await getDb();
  const all = await db.getAll(DRAFT_STORE);
  return all.sort((a, b) => a.seq - b.seq);
}

/** Queue a walk-in / booking made while offline. Returns the draft with its future row id. */
export async function enqueueAppointmentDraft(
  input: Omit<PendingAppointmentDraft, "id" | "seq" | "queued_at" | "attempts">,
): Promise<PendingAppointmentDraft> {
  const db = await getDb();
  const existing = await db.getAll(DRAFT_STORE);
  const seq = existing.reduce((m, i) => Math.max(m, i.seq), 0) + 1;
  const draft: PendingAppointmentDraft = {
    ...input,
    id: crypto.randomUUID(),
    seq,
    queued_at: Date.now(),
    attempts: 0,
  };
  await db.put(DRAFT_STORE, draft);
  await emit();
  requestQueueBackgroundSync();
  return draft;
}

export async function discardAppointmentDraft(id: string) {
  const db = await getDb();
  await db.delete(DRAFT_STORE, id);
  await emit();
}


export async function enqueueQueueAction(
  input: Omit<PendingQueueAction, "id" | "seq" | "queued_at" | "attempts">,
): Promise<PendingQueueAction> {
  const db = await getDb();
  const existing = await db.getAll(STORE);
  const seq = existing.reduce((m, i) => Math.max(m, i.seq), 0) + 1;
  const item: PendingQueueAction = {
    ...input,
    id: crypto.randomUUID(),
    seq,
    queued_at: Date.now(),
    attempts: 0,
  };
  await db.put(STORE, item);
  trackSync({ source: "queue", ref: item.id, status: "queued", table: "appointments", op: "update", label: `Queue status → ${item.status}` });
  await emit();
  requestQueueBackgroundSync();
  return item;
}

export async function discardQueueAction(id: string) {
  const db = await getDb();
  await db.delete(STORE, id);
  await emit();
}

export async function clearQueueOutbox() {
  const db = await getDb();
  await db.clear(STORE);
  await db.clear(DRAFT_STORE);
  await emit();
}

/** Latest queued status per appointment — used as an optimistic overlay in the UI. */
export function overlayStatuses(items: PendingQueueAction[]): Record<string, string> {
  const map: Record<string, string> = {};
  for (const i of items) map[i.appointment_id] = i.status; // sorted by seq → last wins
  return map;
}

function isTransient(message: string) {
  const m = message.toLowerCase();
  return (
    m.includes("failed to fetch") ||
    m.includes("network") ||
    m.includes("timeout") ||
    m.includes("fetch failed") ||
    m.includes("load failed") ||
    m.includes("503") ||
    m.includes("502") ||
    m.includes("504")
  );
}

async function pendingTotal() {
  const [a, d] = await Promise.all([listPendingQueueActions(), listPendingAppointments()]);
  return a.length + d.length;
}

let syncing = false;

/** Broadcast replay progress so the UI can show a real "syncing…" state. */
export interface QueueSyncState {
  syncing: boolean;
  /** items in this replay batch */
  total: number;
  /** items already pushed in this batch */
  done: number;
  lastSyncedAt: number | null;
  lastError: string | null;
}

let syncState: QueueSyncState = {
  syncing: false,
  total: 0,
  done: 0,
  lastSyncedAt: null,
  lastError: null,
};
const syncListeners = new Set<(s: QueueSyncState) => void>();

export function subscribeQueueSyncState(fn: (s: QueueSyncState) => void): () => void {
  syncListeners.add(fn);
  fn(syncState);
  return () => { syncListeners.delete(fn); };
}

export function getQueueSyncState() {
  return syncState;
}

function setSyncState(patch: Partial<QueueSyncState>) {
  syncState = { ...syncState, ...patch };
  syncListeners.forEach((l) => l(syncState));
}

export interface QueueSyncResult {
  synced: number;
  failed: number;
  stillPending: number;
}

export async function syncQueueOutbox(): Promise<QueueSyncResult> {
  if (syncing) return { synced: 0, failed: 0, stillPending: await pendingTotal() };
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return { synced: 0, failed: 0, stillPending: await pendingTotal() };
  }
  syncing = true;
  let synced = 0;
  let failed = 0;
  setSyncState({ syncing: true, total: await pendingTotal(), done: 0, lastError: null });
  try {

    const db = await getDb();

    // 1. Offline-captured walk-ins / bookings first, so later status actions
    //    can reference rows that now exist. The client-generated id makes the
    //    insert idempotent: a duplicate-key error means it already landed.
    const drafts = await listPendingAppointments();
    for (const draft of drafts) {
      const { error } = await supabase.from("appointments").insert({
        id: draft.id,
        user_id: draft.user_id,
        patient_id: draft.patient_id,
        date: draft.date,
        time: draft.time,
        type: draft.type,
        status: "Scheduled",
        ...(draft.chamber_id ? { chamber_id: draft.chamber_id } : {}),
      } as any);

      if (!error || error.code === "23505") {
        await db.delete(DRAFT_STORE, draft.id);
        trackSync({ source: "queue", ref: draft.id, status: "synced", table: "appointments", op: "insert", label: "Offline walk-in", attempt: draft.attempts + 1 });
        synced++;
        setSyncState({ done: synced + failed });
        continue;
      }
      const dmsg = error.message || "Unknown error";
      trackSync({ source: "queue", ref: draft.id, status: "retry", table: "appointments", op: "insert", label: "Offline walk-in", attempt: draft.attempts + 1, error: dmsg });
      if (isTransient(dmsg)) {
        await db.put(DRAFT_STORE, { ...draft, attempts: draft.attempts + 1, last_error: dmsg });
        failed++;
        setSyncState({ done: synced + failed, lastError: dmsg });
        break;
      }
      await db.put(DRAFT_STORE, { ...draft, attempts: draft.attempts + 1, last_error: dmsg });
      failed++;
      setSyncState({ done: synced + failed, lastError: dmsg });
      console.error("appointment draft permanent failure:", dmsg, draft);
    }

    const items = await listPendingQueueActions();
    for (const item of items) {

      const { error } = await supabase
        .from("appointments")
        .update({ status: item.status })
        .eq("id", item.appointment_id);

      if (!error) {
        await db.delete(STORE, item.id);
        trackSync({ source: "queue", ref: item.id, status: "synced", table: "appointments", op: "update", label: `Queue status → ${item.status}`, attempt: item.attempts + 1 });
        synced++;
        setSyncState({ done: synced + failed });
        continue;
      }
      const msg = error.message || "Unknown error";
      trackSync({ source: "queue", ref: item.id, status: isTransient(msg) ? "retry" : "dropped", table: "appointments", op: "update", label: `Queue status → ${item.status}`, attempt: item.attempts + 1, error: msg });
      if (isTransient(msg)) {
        await db.put(STORE, { ...item, attempts: item.attempts + 1, last_error: msg });
        failed++;
        setSyncState({ done: synced + failed, lastError: msg });
        break; // preserve ordering — stop on first transient failure
      }
      // Permanent (RLS, missing row, invalid status): drop it so the queue can drain.
      await db.delete(STORE, item.id);
      failed++;
      setSyncState({ done: synced + failed, lastError: msg });
      console.error("queue outbox permanent failure:", msg, item);
    }
  } finally {
    syncing = false;
    setSyncState({
      syncing: false,
      lastSyncedAt: synced > 0 ? Date.now() : syncState.lastSyncedAt,
    });
    await emit();
  }

  const stillPending = await pendingTotal();
  return { synced, failed, stillPending };
}

export function requestQueueBackgroundSync() {
  if (typeof window === "undefined") return;
  if (navigator.onLine) void syncQueueOutbox();
}

let wired = false;
export function initQueueOutboxAutoSync() {
  if (wired || typeof window === "undefined") return;
  wired = true;
  window.addEventListener("online", () => { void syncQueueOutbox(); });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && navigator.onLine) void syncQueueOutbox();
  });
  if (navigator.onLine) void syncQueueOutbox();
}
